import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Database, Plus, Trash, PencilSimple, Key, Copy, Eye, EyeSlash,
  ShieldCheck, Clock, ArrowsClockwise, DownloadSimple, ChartBar,
  X, Check, Warning, Lightning, Rows,
} from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

var RECORD_TYPES = [
  { key: 'query_result', label: 'Query Results', color: '#4285F4' },
  { key: 'integration_event', label: 'Integration Events', color: '#0ABFA3' },
  { key: 'job_execution', label: 'Job Executions', color: '#7F77DD' },
  { key: 'payload_summary', label: 'Payload Summaries', color: '#D4A574' },
  { key: 'schema_snapshot', label: 'Schema Snapshots', color: '#C8A030' },
  { key: 'matr_event', label: 'MATR Events', color: '#E24B4A' },
];

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

/* ── Policy Form ── */
function PolicyForm({ initial, onSave, onCancel }) {
  var [name, setName] = useState(initial ? initial.policy_name : '');
  var [types, setTypes] = useState(initial ? initial.record_types : []);
  var [days, setDays] = useState(initial ? initial.retention_days : 90);
  var [pii, setPii] = useState(initial ? initial.scrub_pii : true);

  function toggleType(t) {
    setTypes(function (prev) {
      return prev.indexOf(t) >= 0 ? prev.filter(function (x) { return x !== t; }) : prev.concat([t]);
    });
  }

  return (
    <div className="space-y-3 p-4 bg-kova-bg border border-kova-cyan/12" data-testid="policy-form">
      <input value={name} onChange={function (e) { setName(e.target.value); }} placeholder="Policy name" className="w-full bg-kova-card border border-kova-cyan/12 text-sm font-mono text-white px-3 py-2 focus:outline-none focus:border-kova-cyan/50" data-testid="policy-name-input" />
      <div>
        <div className="text-[10px] font-mono text-kova-slate uppercase mb-2">Record Types</div>
        <div className="flex flex-wrap gap-2">
          {RECORD_TYPES.map(function (rt) {
            var on = types.indexOf(rt.key) >= 0;
            return (
              <button key={rt.key} onClick={function () { toggleType(rt.key); }} className={'text-[9px] font-mono px-2 py-1 border transition-colors ' + (on ? 'border-[' + rt.color + ']/50 text-white bg-[' + rt.color + ']/15' : 'border-kova-cyan/12 text-kova-slate')} data-testid={'policy-type-' + rt.key}>
                {rt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] font-mono text-kova-slate uppercase mb-1">Retention (days)</div>
          <input type="number" value={days} onChange={function (e) { setDays(parseInt(e.target.value) || 90); }} className="w-full bg-kova-card border border-kova-cyan/12 text-sm font-mono text-white px-3 py-2 focus:outline-none focus:border-kova-cyan/50" data-testid="policy-days-input" />
          <div className="text-[8px] font-mono text-kova-slate mt-0.5">-1 = indefinite</div>
        </div>
        <div className="flex items-center gap-2 pt-4">
          <Switch checked={pii} onCheckedChange={setPii} data-testid="policy-pii-toggle" />
          <span className="text-[10px] font-mono text-kova-silv">Scrub PII</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={function () { if (!name || types.length === 0) { toast.error('Name and at least one record type required'); return; } onSave({ policy_name: name, record_types: types, retention_days: days, scrub_pii: pii }); }} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[9px] uppercase" data-testid="save-policy-btn">Save</Button>
        <Button onClick={onCancel} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase">Cancel</Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════
   MAIN RETENTION PAGE
   ═══════════════════════════════ */
export default function RetentionManagement() {
  var [policies, setPolicies] = useState([]);
  var [tokens, setTokens] = useState([]);
  var [health, setHealth] = useState(null);
  var [records, setRecords] = useState({ records: [], total: 0 });
  var [loading, setLoading] = useState(true);
  var [showAddPolicy, setShowAddPolicy] = useState(false);
  var [editingPolicy, setEditingPolicy] = useState(null);
  var [showAddToken, setShowAddToken] = useState(false);
  var [tokenLabel, setTokenLabel] = useState('');
  var [createdToken, setCreatedToken] = useState(null);
  var [recordFilter, setRecordFilter] = useState('');

  var fetchAll = useCallback(function () {
    setLoading(true);
    Promise.all([
      fetch(API + '/retention/policies', { credentials: 'include' }).then(function (r) { return r.ok ? r.json() : []; }),
      fetch(API + '/retention/tokens', { credentials: 'include' }).then(function (r) { return r.ok ? r.json() : []; }),
      fetch(API + '/retention/health', { credentials: 'include' }).then(function (r) { return r.ok ? r.json() : null; }),
      fetch(API + '/retention/records?limit=20', { credentials: 'include' }).then(function (r) { return r.ok ? r.json() : { records: [], total: 0 }; }),
    ]).then(function (results) {
      setPolicies(results[0]);
      setTokens(results[1]);
      setHealth(results[2]);
      setRecords(results[3]);
    }).catch(function () {})
      .finally(function () { setLoading(false); });
  }, []);

  useEffect(function () { fetchAll(); }, [fetchAll]);

  function createPolicy(data) {
    fetch(API + '/retention/policies', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify(data),
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (pol) {
        if (pol) { setPolicies(function (p) { return [pol].concat(p); }); setShowAddPolicy(false); toast.success('Policy created'); }
      }).catch(function () { toast.error('Failed'); });
  }

  function updatePolicy(policyId, data) {
    fetch(API + '/retention/policies/' + policyId, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify(data),
    }).then(function (r) { if (r.ok) { fetchAll(); setEditingPolicy(null); toast.success('Updated'); } })
      .catch(function () { toast.error('Failed'); });
  }

  function deletePolicy(policyId) {
    fetch(API + '/retention/policies/' + policyId, { method: 'DELETE', credentials: 'include' })
      .then(function (r) { if (r.ok) { setPolicies(function (p) { return p.filter(function (x) { return x.policy_id !== policyId; }); }); toast.success('Deleted'); } })
      .catch(function () {});
  }

  function togglePolicy(policyId, enabled) {
    fetch(API + '/retention/policies/' + policyId, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ enabled: !enabled }),
    }).then(function (r) {
      if (r.ok) { setPolicies(function (p) { return p.map(function (x) { return x.policy_id === policyId ? Object.assign({}, x, { enabled: !enabled }) : x; }); }); }
    }).catch(function () {});
  }

  function createToken() {
    if (!tokenLabel.trim()) { toast.error('Label required'); return; }
    fetch(API + '/retention/tokens', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ label: tokenLabel }),
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (tok) {
        if (tok) { setCreatedToken(tok.token_value); setTokenLabel(''); setShowAddToken(false); fetchAll(); toast.success('Token created — copy it now, it won\'t be shown again'); }
      }).catch(function () { toast.error('Failed'); });
  }

  function revokeToken(tokenId) {
    fetch(API + '/retention/tokens/' + tokenId, { method: 'DELETE', credentials: 'include' })
      .then(function (r) { if (r.ok) { setTokens(function (t) { return t.filter(function (x) { return x.token_id !== tokenId; }); }); toast.success('Revoked'); } })
      .catch(function () {});
  }

  function filterRecords(type) {
    setRecordFilter(type);
    var url = API + '/retention/records?limit=20';
    if (type) url += '&record_type=' + type;
    fetch(url, { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : { records: [], total: 0 }; })
      .then(setRecords)
      .catch(function () {});
  }

  if (loading) return <div className="p-8 text-center font-mono text-kova-slate">Loading retention data...</div>;

  var usedPct = health ? Math.min(100, (health.storage_bytes_used / health.storage_limit_bytes) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="retention-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-lg">Data Retention</h1>
          <p className="text-[10px] font-mono text-kova-slate mt-0.5">F-008 — Retention policies, reporting tokens, and storage management</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={function () { fetch(API + '/retention/purge', { method: 'POST', credentials: 'include' }).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) { if (d) { toast.success('Purged ' + d.purged + ' expired records'); fetchAll(); } }); }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase h-7 px-3" data-testid="purge-btn">
            <ArrowsClockwise size={10} className="mr-1" />Purge Expired
          </Button>
          <a href={API.replace('/api', '') + '/api/reporting/v1/openapi.json'} target="_blank" rel="noreferrer" className="text-[9px] font-mono px-3 py-1.5 border border-kova-cyan/12 text-kova-slate hover:text-kova-cyan hover:border-kova-cyan/30 transition-colors flex items-center gap-1" data-testid="openapi-spec-link">
            <DownloadSimple size={10} />OpenAPI Spec
          </a>
        </div>
      </div>

      {/* Storage Health */}
      {health && (
        <div className="card-brutal p-4" data-testid="storage-health-card">
          <div className="flex items-center gap-2 mb-3">
            <ChartBar size={14} className="text-kova-cyan" />
            <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Storage Health</span>
          </div>
          <div className="grid grid-cols-4 gap-4 mb-3">
            <div>
              <div className="text-2xl font-heading font-bold">{formatBytes(health.storage_bytes_used)}</div>
              <div className="text-[9px] font-mono text-kova-slate">of {formatBytes(health.storage_limit_bytes)}</div>
            </div>
            {Object.entries(health.record_counts || {}).map(function (e) {
              var rt = RECORD_TYPES.find(function (r) { return r.key === e[0]; });
              return (
                <div key={e[0]}>
                  <div className="text-lg font-heading font-bold">{e[1]}</div>
                  <div className="text-[9px] font-mono" style={{ color: rt ? rt.color : '#3D5A70' }}>{rt ? rt.label : e[0]}</div>
                </div>
              );
            })}
          </div>
          <div className="h-2 bg-kova-cyan/12 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: usedPct + '%', background: usedPct > 80 ? '#E24B4A' : usedPct > 60 ? '#C8A030' : '#0ABFA3' }} />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[8px] font-mono text-kova-slate">{usedPct.toFixed(1)}% used</span>
            {health.oldest_record_at && <span className="text-[8px] font-mono text-kova-slate">Oldest: {new Date(health.oldest_record_at).toLocaleDateString()}</span>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* ═══ Retention Policies ═══ */}
        <div className="card-brutal overflow-hidden" data-testid="policies-section">
          <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center justify-between bg-kova-card">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-kova-cyan" />
              <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Retention Policies</span>
            </div>
            <Button onClick={function () { setShowAddPolicy(!showAddPolicy); setEditingPolicy(null); }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase h-6 px-2" data-testid="add-policy-btn">
              <Plus size={10} className="mr-1" />{showAddPolicy ? 'Cancel' : 'Add Policy'}
            </Button>
          </div>
          <AnimatePresence>
            {showAddPolicy && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <PolicyForm onSave={createPolicy} onCancel={function () { setShowAddPolicy(false); }} />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="divide-y divide-kova-cyan/6 max-h-[400px] overflow-y-auto">
            {policies.length === 0 ? (
              <div className="p-6 text-center text-xs font-mono text-kova-slate">No retention policies. Records won't be retained until a policy is created.</div>
            ) : policies.map(function (p) {
              if (editingPolicy === p.policy_id) {
                return <PolicyForm key={p.policy_id} initial={p} onSave={function (data) { updatePolicy(p.policy_id, data); }} onCancel={function () { setEditingPolicy(null); }} />;
              }
              return (
                <div key={p.policy_id} className="px-4 py-3 flex items-center gap-3" data-testid={'policy-' + p.policy_id}>
                  <span className={'w-2 h-2 rounded-full shrink-0 ' + (p.enabled ? 'bg-kova-green' : 'bg-kova-slate')} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono text-kova-silv">{p.policy_name}</div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {p.record_types.map(function (rt) {
                        var info = RECORD_TYPES.find(function (r) { return r.key === rt; });
                        return <span key={rt} className="text-[7px] font-mono px-1 py-0.5 border" style={{ borderColor: info ? info.color + '40' : 'rgba(10,191,163,0.12)', color: info ? info.color : '#3D5A70' }}>{info ? info.label : rt}</span>;
                      })}
                      <span className="text-[7px] font-mono text-kova-slate">{p.retention_days === -1 ? 'Forever' : p.retention_days + 'd'}</span>
                      {p.scrub_pii && <span className="text-[7px] font-mono text-kova-green">PII Scrub</span>}
                    </div>
                  </div>
                  <button onClick={function () { togglePolicy(p.policy_id, p.enabled); }} className={'text-[8px] font-mono px-2 py-0.5 border transition-colors ' + (p.enabled ? 'border-kova-green/30 text-kova-green' : 'border-kova-cyan/12 text-kova-slate')} data-testid={'toggle-policy-' + p.policy_id}>{p.enabled ? 'On' : 'Off'}</button>
                  <button onClick={function () { setEditingPolicy(p.policy_id); }} className="text-kova-slate hover:text-white transition-colors"><PencilSimple size={12} /></button>
                  <button onClick={function () { deletePolicy(p.policy_id); }} className="text-kova-slate hover:text-kova-red transition-colors"><Trash size={12} /></button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══ Reporting Tokens ═══ */}
        <div className="card-brutal overflow-hidden" data-testid="tokens-section">
          <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center justify-between bg-kova-card">
            <div className="flex items-center gap-2">
              <Key size={14} className="text-kova-cyan" />
              <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Reporting Tokens</span>
            </div>
            <Button onClick={function () { setShowAddToken(!showAddToken); setCreatedToken(null); }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase h-6 px-2" data-testid="add-token-btn">
              <Plus size={10} className="mr-1" />{showAddToken ? 'Cancel' : 'New Token'}
            </Button>
          </div>
          {/* Token Created Banner */}
          <AnimatePresence>
            {createdToken && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="p-3 bg-kova-cyan/10 border-b border-kova-cyan/20" data-testid="created-token-banner">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] font-mono text-kova-cyan uppercase">Copy this token now — it won't be shown again</span>
                    <button onClick={function () { setCreatedToken(null); }} className="text-kova-slate hover:text-white"><X size={12} /></button>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] font-mono text-white bg-kova-bg px-2 py-1 border border-kova-cyan/12 flex-1 overflow-x-auto" data-testid="created-token-value">{createdToken}</code>
                    <button onClick={function () { navigator.clipboard.writeText(createdToken).then(function () { toast.success('Copied'); }); }} className="text-kova-cyan hover:bg-kova-cyan/10 px-2 py-1 transition-colors"><Copy size={14} /></button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showAddToken && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="p-4 space-y-2 border-b border-kova-cyan/12 bg-kova-bg" data-testid="add-token-form">
                  <input value={tokenLabel} onChange={function (e) { setTokenLabel(e.target.value); }} placeholder="Token label (e.g., Tableau Production)" className="w-full bg-kova-card border border-kova-cyan/12 text-xs font-mono text-white px-3 py-1.5 focus:outline-none focus:border-kova-cyan/50" data-testid="token-label-input" />
                  <Button onClick={createToken} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[9px] uppercase" data-testid="generate-token-btn">Generate Token</Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="divide-y divide-kova-cyan/6 max-h-[400px] overflow-y-auto">
            {tokens.length === 0 ? (
              <div className="p-6 text-center text-xs font-mono text-kova-slate">No reporting tokens. Create one to allow external BI tools to query retained data.</div>
            ) : tokens.map(function (t) {
              return (
                <div key={t.token_id} className="px-4 py-3 flex items-center gap-3" data-testid={'token-' + t.token_id}>
                  <Key size={12} className="text-kova-slate shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono text-kova-silv">{t.label}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className="text-[8px] font-mono text-kova-slate">{t.token_value_masked}</code>
                      <span className="text-[7px] font-mono text-kova-slate">expires {new Date(t.expires_at).toLocaleDateString()}</span>
                      {t.last_used_at && <span className="text-[7px] font-mono text-kova-slate">last used {new Date(t.last_used_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <button onClick={function () { revokeToken(t.token_id); }} className="text-kova-slate hover:text-kova-red transition-colors" title="Revoke"><Trash size={14} /></button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ Recent Retained Records ═══ */}
      <div className="card-brutal overflow-hidden" data-testid="retained-records-section">
        <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center justify-between bg-kova-card">
          <div className="flex items-center gap-2">
            <Rows size={14} className="text-kova-cyan" />
            <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Retained Records</span>
            <span className="text-[9px] font-mono text-kova-slate">({records.total} total)</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={function () { filterRecords(''); }} className={'text-[8px] font-mono px-2 py-0.5 border transition-colors ' + (!recordFilter ? 'border-kova-cyan/30 text-kova-cyan' : 'border-kova-cyan/12 text-kova-slate')}>All</button>
            {RECORD_TYPES.map(function (rt) {
              return <button key={rt.key} onClick={function () { filterRecords(rt.key); }} className={'text-[8px] font-mono px-2 py-0.5 border transition-colors ' + (recordFilter === rt.key ? 'text-white' : 'text-kova-slate border-kova-cyan/12')} style={recordFilter === rt.key ? { borderColor: rt.color + '50', color: rt.color } : {}} data-testid={'filter-' + rt.key}>{rt.label.split(' ')[0]}</button>;
            })}
          </div>
        </div>
        <div className="max-h-[350px] overflow-y-auto">
          {records.records.length === 0 ? (
            <div className="p-6 text-center text-xs font-mono text-kova-slate">No retained records yet. Records are automatically retained when matching policies exist.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-kova-card/50 sticky top-0">
                <tr>
                  <th className="text-[8px] font-mono text-kova-slate uppercase text-left px-4 py-2">Type</th>
                  <th className="text-[8px] font-mono text-kova-slate uppercase text-left px-4 py-2">Source</th>
                  <th className="text-[8px] font-mono text-kova-slate uppercase text-left px-4 py-2">Occurred</th>
                  <th className="text-[8px] font-mono text-kova-slate uppercase text-left px-4 py-2">Size</th>
                  <th className="text-[8px] font-mono text-kova-slate uppercase text-left px-4 py-2">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kova-cyan/4">
                {records.records.map(function (r) {
                  var info = RECORD_TYPES.find(function (rt) { return rt.key === r.record_type; });
                  return (
                    <tr key={r.record_id} className="hover:bg-white/[0.02] transition-colors" data-testid={'record-' + r.record_id}>
                      <td className="px-4 py-2"><span className="text-[9px] font-mono px-1.5 py-0.5 border" style={{ borderColor: info ? info.color + '40' : 'rgba(10,191,163,0.12)', color: info ? info.color : '#7A9AB0' }}>{info ? info.label : r.record_type}</span></td>
                      <td className="px-4 py-2 text-[9px] font-mono text-kova-silv">{r.source_feature}</td>
                      <td className="px-4 py-2 text-[9px] font-mono text-kova-slate">{new Date(r.occurred_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-[9px] font-mono text-kova-slate">{formatBytes(r.size_bytes)}</td>
                      <td className="px-4 py-2 text-[9px] font-mono text-kova-slate">{r.expires_at ? new Date(r.expires_at).toLocaleDateString() : 'Never'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
